import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { SelectionModel } from '@angular/cdk/collections';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { DateTime } from 'luxon';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  SgTableActionEvent,
  SgTableCellEditEvent,
  SgTableColumn,
  SgTableColumnResizeEvent,
  SgTableConfig,
  SgTableExpandEvent,
  SgTablePageEvent,
  SgTableSortDirection,
  SgTableSortEvent,
} from './sg-table.model';

@Component({
  selector: 'sg-table',
  templateUrl: './sg-table.component.html',
  styleUrls: ['./sg-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  encapsulation: ViewEncapsulation.None, // Required for styles to apply to Material Table elements
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0', visibility: 'hidden' })),
      state('expanded', style({ height: '*', visibility: 'visible' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class SgTableComponent<T = any> implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() data: T[] = [];
  @Input() config: SgTableConfig = { columns: [] };
  @Input() loading: boolean = false;
  /**
   * When `true`, existing table rows remain visible behind the loading overlay instead of being
   * cleared. Use this for in-place reloads (e.g. after a row action) where hiding the data would
   * cause jarring flicker. Leave at the default `false` when loading a completely new data set
   * (e.g. navigating between tabs) to prevent stale rows from the previous context showing through.
   */
  @Input() keepDataOnLoading: boolean = false;
  @Input() totalCount: number = 0;
  @Input() currentPage: number = 0;
  @Input() pageSize: number = 10;
  /**
   * Set to `false` before the first API call (e.g. tables that require a filter submit before
   * loading). The table wrapper is hidden until this is `true`. Once `true` (the default),
   * an empty data array shows table headers + an inline empty-state row.
   */
  @Input() initialized: boolean = true;
  /**
   * When > 0 and `loading` is true, the table renders this many stub rows with a skeleton bar
   * in every cell (instead of the spinner overlay) — preserves the table layout while loading.
   */
  @Input() skeletonRowCount: number = 0;

  @Output() sortChange = new EventEmitter<SgTableSortEvent>();
  @Output() pageChange = new EventEmitter<SgTablePageEvent>();
  @Output() rowClick = new EventEmitter<T>();
  @Output() actionClick = new EventEmitter<SgTableActionEvent<T>>();
  @Output() columnResize = new EventEmitter<SgTableColumnResizeEvent>();
  @Output() expandChange = new EventEmitter<SgTableExpandEvent<T>>();
  @Output() selectionChange = new EventEmitter<T[]>();
  @Output() cellEdit = new EventEmitter<SgTableCellEditEvent<T>>();

  @ContentChild('rowActions') rowActionsTemplate?: TemplateRef<any>;
  @ContentChild('expandedRow') expandedRowTemplate?: TemplateRef<any>;
  /**
   * Fallback cell template for all `cellType: 'custom'` columns without a per-column `cellRenderer`.
   * Context: `{ $implicit: row, col: column, colKey: string, value: any }`.
   *
   * Use `colKey` for clean @switch rendering:
   * ```html
   * <ng-template #cell let-row let-key="colKey" let-val="value">
   *   @switch (key) {
   *     @case ('status') { <sg-status-tag [status]="val"></sg-status-tag> }
   *     @case ('name')   { <strong>{{ row.name }}</strong> }
   *   }
   * </ng-template>
   * ```
   * For tables where each custom column needs a distinct template, use `column.cellRenderer` instead.
   */
  @ContentChild('cell') cellTemplate?: TemplateRef<any>;

  @ViewChild(MatPaginator) paginator?: MatPaginator;
  @ViewChild(MatSort) sort?: MatSort;

  dataSource = new MatTableDataSource<T>();
  displayedColumns: string[] = [];
  columnWidths: Map<string, number> = new Map();
  resizing = false;

  selection = new SelectionModel<T>(true, []);

  // Single-expand mode: the one currently expanded row
  expandedRow: T | null = null;
  // Multi-expand mode: set of expanded rows
  expandedRows = new Set<T>();

  /** Predicate used by mat-table to decide which rows get the detail-row definition */
  isDetailRow = (_index: number, _row: T): boolean => !!this.config?.expandable;

  private destroy$ = new Subject<void>();
  private resizeColumn: string | null = null;
  private resizeStartX = 0;
  private resizeStartWidth = 0;
  private resizeMoved = false;
  /** Hash of structural config properties; used to skip redundant displayedColumns rebuilds. */
  private _setupHash: string | null = null;

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.setupTable();
    this.updateDataSource();
    this.setupFilterPredicate();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['loading']) {
      if (this.loading) {
        if (this.showSkeleton) {
          // Render stub rows so each cell can show a skeleton bar (preserves the layout).
          this.dataSource.data = this.skeletonStubRows;
        } else if (!this.keepDataOnLoading) {
          // New fetch starting — clear stale rows immediately so the spinner overlay
          // shows on an empty table rather than over the previous result set.
          // Skip when keepDataOnLoading=true (in-place reload) so existing rows stay
          // visible behind the overlay instead of disappearing.
          this.dataSource.data = [];
        }
      } else {
        // Loading finished — restore the real rows (replaces any skeleton stubs).
        this.updateDataSource();
      }
      this.cdr.markForCheck();
    }

    if (changes['data'] && !changes['data'].firstChange) {
      this.updateDataSource();
      this.cdr.markForCheck();
    }

    if (changes['config'] && !changes['config'].firstChange) {
      this.setupTable();
      this.cdr.markForCheck();
    }
  }

  ngAfterViewInit(): void {
    if (!this.config.serverSide && this.paginator) {
      this.dataSource.paginator = this.paginator;
    }

    if (!this.config.serverSide && this.sort) {
      this.dataSource.sort = this.sort;
      this.setupCustomSortData();
    }

    if (this.sort) {
      this.sort.sortChange.pipe(takeUntil(this.destroy$)).subscribe((sort: Sort) => this.onSortChange(sort));
    }

    // Client-side: intercept the filter setter so page resets whenever the toolbar filter changes,
    // preventing "no results" on page 2+ when a filter reduces the total row count.
    if (!this.config.serverSide && this.paginator) {
      this.interceptFilterForPaginationReset();
    }
  }

  transformToString(value: any): string {
    return value != null ? String(value) : '';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    // Safety cleanup: remove resize listeners if the component is destroyed mid-drag
    document.removeEventListener('mousemove', this.onResize);
    document.removeEventListener('mouseup', this.onResizeEnd);
  }

  // ---------------------------------------------------------------------------
  // Expand / collapse rows
  // ---------------------------------------------------------------------------

  toggleExpand(row: T, event: Event): void {
    event.stopPropagation();
    const wasExpanded = this.isExpanded(row);

    if (this.config.multiExpand) {
      if (wasExpanded) {
        this.expandedRows.delete(row);
      } else {
        this.expandedRows.add(row);
      }
    } else {
      this.expandedRow = wasExpanded ? null : row;
    }

    this.expandChange.emit({ row, expanded: !wasExpanded });
    this.cdr.markForCheck();
  }

  isExpanded(row: T): boolean {
    return this.config.multiExpand ? this.expandedRows.has(row) : this.expandedRow === row;
  }

  // ---------------------------------------------------------------------------
  // Row selection
  // ---------------------------------------------------------------------------

  isAllSelected(): boolean {
    return this.selection.selected.length === this.dataSource.data.length && this.dataSource.data.length > 0;
  }

  masterToggle(): void {
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      this.dataSource.data.forEach((row) => this.selection.select(row));
    }
    this.selectionChange.emit([...this.selection.selected]);
    this.cdr.markForCheck();
  }

  selectRow(row: T, event: MouseEvent): void {
    event.stopPropagation();
    this.selection.toggle(row);
    this.selectionChange.emit([...this.selection.selected]);
    this.cdr.markForCheck();
  }

  // ---------------------------------------------------------------------------
  // Cell editing
  // ---------------------------------------------------------------------------

  onCellEdit(column: SgTableColumn, row: T, value: any): void {
    this.cellEdit.emit({ column: column.key as string, row, value });
  }

  // ---------------------------------------------------------------------------
  // Setup
  // ---------------------------------------------------------------------------

  private setupTable(): void {
    if (!this.config) return;

    // Skip expensive rebuild if nothing structural changed
    const hash = this.computeSetupHash();
    if (hash === this._setupHash) return;
    this._setupHash = hash;

    this.displayedColumns = this.config.columns.map((col) => col.key as string);

    if (this.config.showRowActions) {
      this.displayedColumns.push('__actions__');
    }

    // Prepend special columns (right-to-left so __select__ stays first)
    if (this.config.expandable) {
      this.displayedColumns.unshift('__expand__');
    }
    if (this.config.selectable) {
      this.displayedColumns.unshift('__select__');
      this.selection = new SelectionModel<T>(this.config.multiSelect !== false, []);
    }

    if (!this.config.pageSize) {
      this.config.pageSize = this.pageSize;
    }

    if (!this.config.pageSizeOptions) {
      this.config.pageSizeOptions = [5, 10, 25, 50];
    }
  }

  /** Serialises only the structural parts of config that affect displayedColumns. */
  private computeSetupHash(): string {
    if (!this.config) return '';
    return JSON.stringify({
      cols: this.config.columns.map((c) => c.key),
      expandable: this.config.expandable,
      selectable: this.config.selectable,
      showRowActions: this.config.showRowActions,
      multiSelect: this.config.multiSelect,
    });
  }

  private updateDataSource(): void {
    this.dataSource.data = this.showSkeleton ? this.skeletonStubRows : this.data;
    this.reconcileSelectionAndExpansion();
    // Client-side: if current page exceeds new data length, reset to first page
    if (!this.config?.serverSide && this.paginator) {
      const pageSize = this.paginator.pageSize || 10;
      if (this.paginator.pageIndex > 0 && this.paginator.pageIndex * pageSize >= (this.data?.length ?? 0)) {
        this.paginator.firstPage();
      }
    }
  }

  /**
   * Drop selection / expansion entries that reference rows no longer in `this.data`.
   * `SelectionModel<T>` uses reference identity, so after a server refetch (which returns
   * fresh row objects) every prior selection is a ghost — `(selectionChange)` consumers
   * would receive references that no longer exist in the table, and `isAllSelected()`
   * would be wrong whenever the new dataset matches the old one in length but not identity.
   */
  private reconcileSelectionAndExpansion(): void {
    const validRows = new Set(this.data);

    const previouslySelected = this.selection.selected;
    const stillSelected = previouslySelected.filter((row) => validRows.has(row));
    if (stillSelected.length !== previouslySelected.length) {
      this.selection.clear();
      if (stillSelected.length > 0) {
        this.selection.select(...stillSelected);
      }
      this.selectionChange.emit([...stillSelected]);
    }

    if (this.expandedRow && !validRows.has(this.expandedRow)) {
      this.expandedRow = null;
    }
    for (const row of this.expandedRows) {
      if (!validRows.has(row)) {
        this.expandedRows.delete(row);
      }
    }
  }

  /**
   * Wraps the dataSource `filter` setter so that any time a filter string is applied
   * (e.g. from sg-table-toolbar), the paginator resets to page 0. This avoids the
   * "0 visible rows on page 2" bug when a filter reduces the total result count.
   * Uses property interception because `filterChange` is not in the public MatTableDataSource API.
   */
  private interceptFilterForPaginationReset(): void {
    const proto = Object.getPrototypeOf(this.dataSource);
    const descriptor = Object.getOwnPropertyDescriptor(proto, 'filter');
    if (!descriptor) return;

    Object.defineProperty(this.dataSource, 'filter', {
      get: () => descriptor.get!.call(this.dataSource),
      set: (value: string) => {
        descriptor.set!.call(this.dataSource, value);
        if (this.paginator && this.paginator.pageIndex > 0) {
          this.paginator.firstPage();
        }
      },
      configurable: true,
    });
  }

  private setupFilterPredicate(): void {
    this.dataSource.filterPredicate = (data: T, filter: string): boolean => {
      return this.flattenToString(data).toLowerCase().includes(filter);
    };
  }

  private flattenToString(value: any): string {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (Array.isArray(value)) return value.map((v) => this.flattenToString(v)).join(' ');
    if (typeof value === 'object')
      return Object.values(value)
        .map((v) => this.flattenToString(v))
        .join(' ');
    return '';
  }

  private setupCustomSortData(): void {
    const hasCustomSorters = this.config.columns.some((col) => col.sortFn);
    if (!hasCustomSorters) return;

    this.dataSource.sortData = (data: T[], sort: MatSort) => {
      const active = sort.active;
      const direction = sort.direction;
      if (!active || direction === '') return data;

      const column = this.config.columns.find((c) => (c.key as string) === active);
      if (column?.sortFn) {
        const multiplier = direction === 'asc' ? 1 : -1;
        return [...data].sort((a, b) => column.sortFn!(a, b) * multiplier);
      }

      // Fallback default sort
      return [...data].sort((a, b) => {
        const valueA = column ? this.getCellValue(a, column) : (a as any)[active];
        const valueB = column ? this.getCellValue(b, column) : (b as any)[active];
        if (valueA == null && valueB == null) return 0;
        if (valueA == null) return direction === 'asc' ? -1 : 1;
        if (valueB == null) return direction === 'asc' ? 1 : -1;
        if (typeof valueA === 'number' && typeof valueB === 'number') {
          return (valueA - valueB) * (direction === 'asc' ? 1 : -1);
        }
        return String(valueA).localeCompare(String(valueB)) * (direction === 'asc' ? 1 : -1);
      });
    };
  }

  // ---------------------------------------------------------------------------
  // Event handlers
  // ---------------------------------------------------------------------------

  onSortChange(sort: Sort): void {
    if (this.config.serverSide) {
      this.sortChange.emit({
        field: sort.active,
        direction: sort.direction as SgTableSortDirection,
      });
    }
  }

  onPageChange(event: any): void {
    if (this.config.serverSide) {
      this.pageChange.emit({
        pageIndex: event.pageIndex,
        pageSize: event.pageSize,
      });
    }
  }

  onRowClick(row: T): void {
    // Ignore clicks on skeleton stub rows while loading.
    if (this.showSkeleton || !this.config.rowClick) return;

    // 'toggle-selection': row click also toggles checkbox selection (in addition to emitting rowClick)
    if (this.config.rowClickMode === 'toggle-selection' && this.config.selectable) {
      this.selection.toggle(row);
      this.selectionChange.emit([...this.selection.selected]);
      this.cdr.markForCheck();
    }

    this.rowClick.emit(row);
  }

  onActionClick(action: string, row: T, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    this.actionClick.emit({
      action,
      row,
      event,
    });
  }

  // ---------------------------------------------------------------------------
  // Styling helpers
  // ---------------------------------------------------------------------------

  getColumnClass(column: SgTableColumn): string {
    const classes: string[] = [`sg-table-cell-${column.cellType || 'text'}`];

    if (column.cssClass) {
      classes.push(column.cssClass);
    }

    if (column.ellipsis) {
      classes.push('sg-table-cell-ellipsis');
    }

    return classes.join(' ');
  }

  getRowClass(row: T): string {
    const classes: string[] = [];

    if (this.config.rowClassFn) {
      const result = this.config.rowClassFn(row);
      const userClasses = Array.isArray(result) ? result : [result];
      classes.push(...userClasses);
    }

    if (this.config.rowBorderStyle?.enabled && this.config.rowBorderStyle.position === 'right') {
      classes.push('sg-table-row-border-right');
    }

    return classes.join(' ');
  }

  getTableStyles(): { [key: string]: string } {
    const styles: { [key: string]: string } = {};

    if (this.config.rowSpacing) {
      styles['border-spacing'] = `0 ${this.config.rowSpacing}`;
    } else if (this.config.rowStyle === 'cards') {
      styles['border-spacing'] = '0 8px';
    }

    if (this.config.rowBorderStyle?.enabled) {
      if (this.config.rowBorderStyle.width) {
        styles['--sg-table-border-width'] = this.config.rowBorderStyle.width;
      }
      if (this.config.rowBorderStyle.color) {
        styles['--sg-table-border-color'] = this.config.rowBorderStyle.color;
      }
    }

    return styles;
  }

  getCellValue(row: T, column: SgTableColumn): any {
    const keys = (column.key as string).split('.');
    let value: any = row;

    for (const key of keys) {
      value = value?.[key];
    }

    return value;
  }

  formatCellValue(value: any, column: SgTableColumn): string {
    if (value == null) {
      return '';
    }

    switch (column.cellType) {
      case 'date':
        return this.formatDate(value, column.dateFormat || 'dd/MM/yyyy');
      case 'number':
        return this.formatNumber(value, column.numberFormat);
      case 'text':
      default:
        return String(value);
    }
  }

  private formatNumber(value: any, format?: string): string {
    const num = Number(value);
    if (isNaN(num)) return String(value);
    if (format === 'raw') return String(value);
    return num.toLocaleString(undefined, { maximumFractionDigits: 6 });
  }

  private formatDate(value: any, format: string): string {
    if (!value) return '';

    let dateTime: DateTime;

    if (value instanceof Date) {
      dateTime = DateTime.fromJSDate(value);
    } else if (typeof value === 'string' || typeof value === 'number') {
      dateTime = DateTime.fromISO(String(value));
      if (!dateTime.isValid) {
        dateTime = DateTime.fromMillis(Number(value));
      }
    } else {
      return String(value);
    }

    if (!dateTime.isValid) {
      return String(value);
    }

    return dateTime.toFormat(format);
  }

  /** True when the table wrapper should be rendered (either initialized or actively loading). */
  get showTable(): boolean {
    return this.initialized || this.loading;
  }

  /** True when loading should render skeleton stub rows instead of the spinner overlay. */
  get showSkeleton(): boolean {
    return this.loading && this.skeletonRowCount > 0;
  }

  /** Empty stub rows used to render skeleton placeholders while loading. */
  get skeletonStubRows(): T[] {
    return Array.from({ length: this.skeletonRowCount }, () => ({}) as T);
  }

  get emptyStateConfig() {
    return (
      this.config?.emptyStateConfig || {
        variant: 'empty',
        title: 'No Results',
        message: 'No data available to display',
      }
    );
  }

  // ---------------------------------------------------------------------------
  // Column resize
  // ---------------------------------------------------------------------------

  onResizeStart(event: MouseEvent, column: SgTableColumn): void {
    if (!column.resizable) return;

    event.preventDefault();
    event.stopPropagation();

    this.resizing = true;
    this.resizeColumn = column.key as string;
    this.resizeStartX = event.clientX;
    this.resizeMoved = false;

    // A column with no declared or dragged width starts from its RENDERED width; the 150px fallback
    // would make it jump on the first mousemove. Sized columns keep their own width: when the table
    // stretches fixed columns to fill spare space, writing the rendered width back would grow it again.
    const storedWidth = this.columnWidths.get(this.resizeColumn);
    const renderedWidth = (event.target as HTMLElement).closest('th')?.getBoundingClientRect().width;
    const currentWidth =
      storedWidth || (column.width ? this.getColumnWidth(column) : renderedWidth) || this.getColumnWidth(column);
    this.resizeStartWidth = currentWidth;

    document.addEventListener('mousemove', this.onResize);
    document.addEventListener('mouseup', this.onResizeEnd);
  }

  private onResize = (event: MouseEvent): void => {
    if (!this.resizing || !this.resizeColumn) return;

    const column = this.config.columns.find((col) => col.key === this.resizeColumn);
    if (!column) return;

    const deltaX = event.clientX - this.resizeStartX;
    if (deltaX !== 0) this.resizeMoved = true;
    let newWidth = this.resizeStartWidth + deltaX;

    const minWidth = parseInt(column.minWidth || '50px', 10);
    const maxWidth = column.maxWidth ? parseInt(column.maxWidth, 10) : Infinity;

    newWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));

    this.columnWidths.set(this.resizeColumn, newWidth);
    this.cdr.markForCheck();
  };

  private onResizeEnd = (): void => {
    if (this.resizing && this.resizeColumn) {
      const finalWidth = this.columnWidths.get(this.resizeColumn);
      if (finalWidth !== undefined) {
        this.columnResize.emit({
          columnKey: this.resizeColumn,
          width: finalWidth,
        });
      }
      if (this.resizeMoved) this.swallowNextClick();
    }

    this.resizing = false;
    this.resizeColumn = null;

    document.removeEventListener('mousemove', this.onResize);
    document.removeEventListener('mouseup', this.onResizeEnd);
  };

  /**
   * A drag that ends inside a sortable header makes the browser fire a click there, which
   * MatSortHeader treats as "sort this column". Eat that one click; the timeout drops the
   * listener if the drag ended where no click follows (e.g. outside the window).
   */
  private swallowNextClick(): void {
    const swallow = (event: MouseEvent): void => {
      event.stopPropagation();
      event.preventDefault();
    };
    document.addEventListener('click', swallow, { capture: true, once: true });
    setTimeout(() => document.removeEventListener('click', swallow, { capture: true }));
  }

  private getColumnWidth(column: SgTableColumn): number {
    if (!column.width) return 150;
    return parseInt(column.width, 10);
  }

  getComputedColumnWidth(column: SgTableColumn): string {
    const key = column.key as string;
    const width = this.columnWidths.get(key);
    return width ? `${width}px` : column.width || 'auto';
  }
}
